import _axios from "axios";
import { AUTH_KEY, ETERNAL_REFRESH_TOKEN } from '../consts/auth'
import { getRefreshTokenInterval, getTokenExpiration } from "./token";
import { getAuthController, logoutUser } from "./auth";

// Single-flight, cross-tab refresh coordination
import { waitForFreshToken } from "./tokenRefreshOrchestrator";

/**
 * Configures the global Axios instance used for all API requests.
 * Includes default headers and JSON response typing.
 */
export const axios = _axios.create({
  responseType: "json",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});


/**
 * Request Interceptor
 * 1. Dispatches global events to show loading spinners for mutating requests (POST, PUT, PATCH, DELETE).
 * 2. Injects the Authorization header from localStorage.
 * 3. Handles 'Skip-Refresh' custom headers for specific queries.
 * 4. Injects the 'X-Whitelabel' header based on environment variables.
 * 5. Rewrites production API URLs to staging URLs when running in development/staging environments.
 */
axios.interceptors.request.use(async (config) => {
  // Show spinner for POST, PUT, PATCH, DELETE requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase())) {
    document.dispatchEvent(new CustomEvent('show-spinner'));
  }

  const tokenType = localStorage.getItem(`${AUTH_KEY}_type`);
  const token = localStorage.getItem(AUTH_KEY);

  // Custom headers take precedence over regular headers
  // This is used to override request token when fetching user data
  if (config.customHeaders) {
    config.headers.Authorization = `${config.customHeaders.Authorization}`;
    // Since we're using a custom header, we know that the token is valid
    // and we don't need to refresh it
    config.headers['Skip-Refresh'] = true;
  } else if (tokenType && token) {
    config.headers.Authorization = `${tokenType} ${token}`;
  }

  config.headers["X-Whitelabel"] = process.env.REACT_APP_WHITELABEL;

  /**
   * Development mode will redirect requests to production (v1)
   * to staging environment,
   */
  const excludeStagingApi = [
    'https://app.socialscavenger.com/v1/upload.json',
    'https://app.socialscavenger.com/v1/medias/sign'
  ]
  if(
    (window.location.hostname === 'staging-builder.socialscavenger.com' ||
    process.env.NODE_ENV === 'development') &&
    !excludeStagingApi.find(url => config.url.match(url))
  ) {
    config.url = config.url.replace('v1', 'staging')
  }
  return config;
});

/**
 * Response Interceptor
 * 1. Dispatches global events to hide loading spinners on success or failure.
 * 2. Catches 401 Unauthorized errors and attempts to silently refresh the JWT token.
 * 3. Coordinates cross-tab token refreshing using `waitForFreshToken` to prevent token ping-pong.
 * 4. Retries the original request if the token refresh is successful.
 * 5. Logs the user out if the token refresh fails or the session is fully expired.
 */
axios.interceptors.response.use(
  response => {
    // Hide spinner on successful response
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(response.config.method?.toUpperCase())) {
      document.dispatchEvent(new CustomEvent('hide-spinner'));
    }
    return response;
  }, 
  async error => { // make handler async to await refresh flow
    // Hide spinner on error
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(error.config?.method?.toUpperCase())) {
      document.dispatchEvent(new CustomEvent('hide-spinner'));
    }
    const originalRequest = error.config;
    const auth = JSON.parse(localStorage.getItem(`${AUTH_KEY}_state`))
    const shouldRefresh = (getTokenExpiration(auth?.token) < getRefreshTokenInterval(auth?.lifespan))
    const isUnauthorized = error?.response?.status === 401
    const isRefreshRequest = originalRequest?.url?.match(/refresh.json$/)
    const isSkipRefresh = originalRequest?.headers?.['Skip-Refresh']

    if (
        isUnauthorized &&
        localStorage.getItem(ETERNAL_REFRESH_TOKEN) &&
        shouldRefresh &&
        // do not retry failed refresh token
        !isRefreshRequest &&
        // do not refresh if skip refresh is set
        !isSkipRefresh &&
        // prevent infinite loop
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        const { signIn } = getAuthController();

        try {
          console.debug(`REFTOKEN: A request returns 401. Refreshing token. Auth state: ${JSON.stringify(auth)}`);
          console.debug(`REFTOKEN: Should refresh token: ${shouldRefresh}`);
          console.debug(`REFTOKEN: Last token received at ${auth?.token_received_at}`);
          console.debug(`REFTOKEN: Last token expired at ${auth?.token_expired_at}`);

          // Coordinate with other tabs to refresh once and avoid token ping-pong
          const authPayload = await waitForFreshToken({ currentAuthState: auth });

          if (!authPayload) {
            console.debug(`REFTOKEN: Refresh token request failed. Response: ${JSON.stringify(authPayload)}`);
            return Promise.reject(error);
          }

          console.debug(`REFTOKEN: Signing on from refresh token (interceptor) with this auth state: ${JSON.stringify(authPayload)}`);
          console.debug(`REFTOKEN: Retrying request with new token: ${authPayload?.token || localStorage.getItem(AUTH_KEY)}`);

          // Persist new token details with react-auth-kit context (if available)
          if (typeof signIn === 'function' && authPayload?.expiresIn) {
            console.debug(`REFTOKEN: Signing in with react-auth-kit`);
            signIn(authPayload);
          }

          // update headers for this retry and future calls
          const nextTokenType = authPayload?.tokenType || localStorage.getItem(`${AUTH_KEY}_type`) || 'Bearer';
          const nextToken = authPayload?.token || localStorage.getItem(AUTH_KEY);
          if (nextToken) {
            originalRequest.headers["Authorization"] = `${nextTokenType} ${nextToken}`;
            axios.defaults.headers.common['Authorization'] = `${nextTokenType} ${nextToken}`;
          }

          return axios.request(originalRequest); // return the retried call so react-query sees the data
        } catch (refreshErr) {
          console.debug(`REFTOKEN: Refresh token request failed. Error: ${refreshErr}`);
          // Wait 10 seconds to allow any parallel refresh process (e.g., in another tab) to complete
          await new Promise((resolve) => setTimeout(resolve, 10000));

          try {
            const authAfterWait = JSON.parse(localStorage.getItem(`${AUTH_KEY}_state`));
            const tokenStillValid = authAfterWait?.token && getTokenExpiration(authAfterWait?.token) > 0;

            if (!tokenStillValid) {
              console.debug("REFTOKEN: Token is invalid or expired after waiting. Signing out user.");
              logoutUser();
            }
          } catch (postRefreshErr) {
            console.debug(`REFTOKEN: Error during post-refresh token validation: ${postRefreshErr}`);
          }

          return Promise.reject(refreshErr);
        }
      }

      // Not eligible for refresh or still unauthorized
      if (isUnauthorized && !isRefreshRequest && shouldRefresh) {
        console.debug(`REFTOKEN: Eternal refresh token: ${localStorage.getItem(ETERNAL_REFRESH_TOKEN)}`);
        console.debug(`REFTOKEN: Request URL: ${originalRequest.url}`);
        console.debug(`REFTOKEN: Not refreshing token`);
        console.debug(`REFTOKEN: Last token received at ${auth?.token_received_at}`);
        console.debug(`REFTOKEN: Last token expired at ${auth?.token_expired_at}`);
        console.debug(`REFTOKEN: Auth state ${JSON.stringify(auth)}`);
      }

      return Promise.reject(error);
    }
)